import Swiper from "swiper";
import { A11y, Autoplay, EffectFade, Keyboard } from "swiper/modules";

let youtubeIframeApiPromise = null;
const youtubePlayerPromises = new WeakMap();
const youtubePlayers = new WeakMap();
const youtubeRevealTimers = new WeakMap();
const youtubePlaybackFallbackTimers = new WeakMap();
const youtubeRevealDelayMs = 600;
// Without the IFrame API there is no `onStateChange` to reveal on, so phones
// give the embed a longer, fixed head start before the frame is faded in.
const youtubeMobileRevealDelayMs = 1500;

function loadYouTubeIframeApi() {
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }

  if (window.YT && typeof window.YT.Player === "function") {
    return Promise.resolve(window.YT);
  }

  if (youtubeIframeApiPromise) {
    return youtubeIframeApiPromise;
  }

  youtubeIframeApiPromise = new Promise((resolve, reject) => {
    const previousReadyHandler = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousReadyHandler === "function") {
        previousReadyHandler();
      }

      resolve(window.YT || null);
    };

    if (!document.querySelector('script[data-youtube-iframe-api="true"]')) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.dataset.youtubeIframeApi = "true";
      script.addEventListener("error", () => {
        reject(new Error("Failed to load YouTube IFrame API"));
      });
      document.head.appendChild(script);
    }
  }).catch(() => null);

  return youtubeIframeApiPromise;
}

document.addEventListener("DOMContentLoaded", function () {
  const heroCarousels = document.querySelectorAll(".hero-carousel");

  heroCarousels.forEach((carousel) => {
    initializeHeroCarousel(carousel);
  });
});

document.addEventListener("blockEditorInit", function (event) {
  const block = event.detail.block;

  if (!block) {
    return;
  }

  if (block.matches && block.matches(".hero-carousel")) {
    initializeHeroCarousel(block);
  }

  block.querySelectorAll(".hero-carousel").forEach((carousel) => {
    initializeHeroCarousel(carousel);
  });
});

function initializeHeroCarousel(carouselElement) {
  const swiperContainer = carouselElement.querySelector(".hero-swiper");
  const navigationCards = Array.from(
    carouselElement.querySelectorAll(".swiper-pagination-card"),
  );
  const mobileProgressFill = carouselElement.querySelector(
    ".hero-carousel__progress-fill",
  );
  const slides = Array.from(carouselElement.querySelectorAll(".swiper-slide"));
  const hasMultipleSlides = slides.length > 1;
  const hasNavigationCards = navigationCards.length > 0;
  const mobileMediaQuery = window.matchMedia("(max-width: 767px)");
  const reducedMotionQuery = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  );

  if (!swiperContainer) {
    console.warn("Hero carousel: Missing Swiper container");
    return;
  }

  if (swiperContainer.dataset.heroCarouselInitialized === "true") {
    return;
  }

  swiperContainer.dataset.heroCarouselInitialized = "true";

  let heroSwiper;
  let spacingUpdatePending = false;
  let lastNavigationHeight = null;
  let heightUpdatePending = false;
  let pauseInactiveMediaTimeout = null;
  const outgoingMediaGraceMs = 1800;
  const youtubeShells = Array.from(
    carouselElement.querySelectorAll(".hero-carousel__video-shell--youtube"),
  );

  const isYouTubeShellActive = (shell) => {
    const slide = shell?.closest(".swiper-slide");

    return Boolean(slide && slide.classList.contains("swiper-slide-active"));
  };

  // Mobile plays the hero videos too, but never while the page is loading: no
  // source is attached and no iframe is mounted until after the load event and
  // the next idle slot, so hero media never competes with LCP or first input.
  // Desktop starts straight away - it was never the PageSpeed bottleneck.
  let mediaStarted = !mobileMediaQuery.matches;
  let mediaStartScheduled = false;
  // Nothing plays, and nothing is fetched, while the hero is off screen.
  let heroInView = true;

  // Visitors who asked for less motion, are on Save-Data, or are on a 2G-class
  // connection keep the poster still instead of the video.
  function prefersLightweightMedia() {
    if (reducedMotionQuery.matches) return true;
    if (!mobileMediaQuery.matches) return false;

    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (!connection) return false;

    return Boolean(
      connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ""),
    );
  }

  function startMediaWhenIdle() {
    if (mediaStarted || mediaStartScheduled) return;

    mediaStartScheduled = true;

    const begin = () => {
      const run = () => {
        mediaStarted = true;
        syncBackgroundVideos();

        if (!mobileMediaQuery.matches) {
          primeSelfHostedSlide((heroSwiper ? heroSwiper.activeIndex : 0) + 1);
        }
      };

      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(run, { timeout: 2000 });
        return;
      }

      window.setTimeout(run, 300);
    };

    if (document.readyState === "complete") {
      begin();
      return;
    }

    window.addEventListener("load", begin, { once: true });
  }

  // The YouTube IFrame API is roughly another megabyte of script on top of the
  // player itself. Phones drive the embed over postMessage instead, which
  // covers everything the carousel asks for - mute, play, pause, seek - and the
  // embed's own `loop=1` handles the replay the API's state events drove.
  const canUseYouTubeApi = () => !mobileMediaQuery.matches;

  const primeYouTubeSlide = (index) => {
    // Warming the next slide costs a second YouTube player download, so it
    // stays a desktop-only optimisation; mobile mounts on slide change.
    if (mobileMediaQuery.matches || prefersLightweightMedia()) return;
    if (index < 0 || index >= slides.length) return;

    const targetSlide = slides[index];
    if (!targetSlide) return;

    const shell = targetSlide.querySelector(
      ".hero-carousel__video-shell--youtube",
    );
    if (!shell) return;

    mountYouTubeIframe(shell, { autoplay: false });
  };

  // Holding a video back leaves a black panel when it has no poster to show,
  // so those slides keep their video even for lightweight visitors. The poster
  // is a sibling <img> rather than the video's `poster` attribute, so that it
  // can carry a srcset - fall back to the attribute for older markup.
  function shouldHoldPoster(video) {
    if (!prefersLightweightMedia() || !video) return false;

    const background = video.closest(".hero-carousel__background");

    return Boolean(
      video.poster ||
      (background && background.querySelector(".hero-carousel__video-poster")),
    );
  }

  // Swap the deferred data-src onto a self-hosted video and begin loading it.
  // No-op once the source is live, so it's safe to call repeatedly.
  function ensureSelfHostedVideoSrc(video, { preload = "auto" } = {}) {
    if (!video || !mediaStarted || shouldHoldPoster(video)) return false;

    const source = video.querySelector("source[data-src]");
    if (!source) return false;

    // Phones take the editor's mobile cut when there is one, so a desktop
    // master never gets pulled down over a mobile connection.
    const mobileSrc = source.dataset.srcMobile;
    const useMobileSrc = Boolean(mobileSrc) && mobileMediaQuery.matches;

    source.src = useMobileSrc ? mobileSrc : source.dataset.src;

    if (useMobileSrc && source.dataset.typeMobile) {
      source.type = source.dataset.typeMobile;
    }

    source.removeAttribute("data-src");
    source.removeAttribute("data-src-mobile");

    if (video.preload === "none") {
      video.preload = preload;
    }

    video.load();

    return true;
  }

  // Begin buffering an upcoming slide's video shortly before it's shown so the
  // background is ready when the carousel advances. Mobile fetches only the
  // metadata up front so the slide that is actually on screen keeps the
  // connection to itself; play() pulls the rest when the slide arrives.
  const primeSelfHostedSlide = (index) => {
    if (index < 0 || index >= slides.length) return;

    const targetSlide = slides[index];
    if (!targetSlide) return;

    ensureSelfHostedVideoSrc(
      targetSlide.querySelector(".hero-carousel__video"),
      { preload: mobileMediaQuery.matches ? "metadata" : "auto" },
    );
  };

  // Chromium can refuse to autoplay a video that was only muted through the
  // attribute, and mobile Safari can reject the first play() while the source
  // is still loading. Set `muted` as a property and retry once there is data.
  function playSelfHostedVideo(video) {
    const attempt = () => {
      video.muted = true;

      const playback = video.play();

      if (playback && typeof playback.catch === "function") {
        playback.catch(() => {});
      }
    };

    attempt();

    if (video.dataset.heroPlaybackBound === "true") return;

    video.dataset.heroPlaybackBound = "true";

    video.addEventListener("playing", () => {
      video.classList.add("hero-carousel__video--playing");

      // On phones the next slide is not fetched until the visible one is
      // genuinely running, so the two never share the connection.
      if (mobileMediaQuery.matches) {
        primeSelfHostedSlide((heroSwiper ? heroSwiper.activeIndex : 0) + 1);
      }
    });

    const retry = () => {
      const slide = video.closest(".swiper-slide");

      if (slide && slide.classList.contains("swiper-slide-active")) {
        attempt();
      }
    };

    video.addEventListener("loadeddata", retry);
    video.addEventListener("canplay", retry);
  }

  heroSwiper = new Swiper(swiperContainer, {
    modules: [A11y, Autoplay, EffectFade, Keyboard],
    slidesPerView: 1,
    spaceBetween: 0,
    loop: false,
    speed: 700,
    autoHeight: false,
    allowTouchMove: hasMultipleSlides,
    autoplay: hasMultipleSlides
      ? {
          delay: 10000,
          disableOnInteraction: false,
          pauseOnMouseEnter: false,
        }
      : false,
    effect: "fade",
    fadeEffect: {
      crossFade: true,
    },
    a11y: {
      enabled: true,
      prevSlideMessage: "Previous slide",
      nextSlideMessage: "Next slide",
      // The navigation cards are a tablist, so each slide is the panel its card
      // controls. Swiper would otherwise stamp role="group" over the markup.
      slideRole: hasNavigationCards ? "tabpanel" : "group",
      // The panels are named by their card via aria-labelledby; Swiper's
      // "1 / 3" label would only add noise next to it.
      slideLabelMessage: hasNavigationCards
        ? null
        : "{{index}} / {{slidesLength}}",
    },
    keyboard: {
      enabled: hasMultipleSlides,
      onlyInViewport: true,
    },
    touchRatio: hasMultipleSlides ? 1 : 0,
    touchAngle: 45,
    simulateTouch: hasMultipleSlides,
    on: {
      init: function () {
        updateNavigationCards(0);
        syncActiveSlideContent();
        startProgressBar(0);
        startMobileProgressBar(0);
        equalizeSlideHeights();
        adjustCarouselSpacing();
        syncBackgroundVideos();

        if (!mobileMediaQuery.matches) {
          primeSelfHostedSlide(1);
        }
      },
      slideChangeTransitionStart: function () {
        if (!hasMultipleSlides) return;

        updateNavigationCards(this.activeIndex);
        resetPreviousProgressBars(this.activeIndex);
        startProgressBar(this.activeIndex);
        startMobileProgressBar(this.activeIndex);
        syncBackgroundVideos({ inactiveDelay: outgoingMediaGraceMs });
        primeYouTubeSlide(this.activeIndex + 1);
        primeSelfHostedSlide(this.activeIndex + 1);
      },
      slideChangeTransitionEnd: function () {
        syncActiveSlideContent();

        resetPreviousProgressBars(this.activeIndex);
      },
      autoplayPause: function () {
        pauseProgressBars();
        pauseMobileProgressBar();
      },
      autoplayResume: function () {
        resumeProgressBars();
        resumeMobileProgressBar();
      },
    },
  });

  if (hasNavigationCards) {
    navigationCards.forEach((card, index) => {
      card.addEventListener("click", () => {
        heroSwiper.slideTo(index);
      });
    });

    // role="tablist" comes with a keyboard contract: the arrows move between
    // the cards and only the selected card stays in the tab order (the roving
    // tabindex is applied in updateNavigationCards).
    const cardsList = carouselElement.querySelector(".hero-carousel__cards");

    if (cardsList) {
      cardsList.addEventListener("keydown", (event) => {
        const lastIndex = navigationCards.length - 1;
        const focusedIndex = navigationCards.indexOf(document.activeElement);
        const fromIndex =
          focusedIndex < 0 ? heroSwiper.activeIndex : focusedIndex;
        let targetIndex;

        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          targetIndex = fromIndex + 1;
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          targetIndex = fromIndex - 1;
        } else if (event.key === "Home") {
          targetIndex = 0;
        } else if (event.key === "End") {
          targetIndex = lastIndex;
        } else {
          return;
        }

        // Swiper's keyboard module listens for the arrows on the document too,
        // so the event has to stop here or the carousel moves twice.
        event.preventDefault();
        event.stopPropagation();

        targetIndex = Math.min(Math.max(targetIndex, 0), lastIndex);
        heroSwiper.slideTo(targetIndex);
        navigationCards[targetIndex].focus();
      });
    }
  }

  function updateNavigationCards(activeIndex) {
    if (!hasNavigationCards) return;

    navigationCards.forEach((card, index) => {
      const isActive = index === activeIndex;

      card.classList.remove(
        "hero-carousel__card--active",
        "hero-carousel__card--progress",
      );

      card.setAttribute("role", "tab");
      card.setAttribute("aria-selected", isActive.toString());
      card.setAttribute("tabindex", isActive ? "0" : "-1");
      card.removeAttribute("aria-pressed");
      card.removeAttribute("aria-label");

      if (isActive) {
        card.classList.add("hero-carousel__card--active");
      }
    });
  }

  function syncActiveSlideContent() {
    const activeSlide =
      carouselElement.querySelector(".swiper-slide-active") || slides[0];

    carouselElement
      .querySelectorAll(".hero-carousel__content")
      .forEach((content) => {
        content.classList.toggle(
          "hero-carousel__content--active",
          Boolean(activeSlide && activeSlide.contains(content)),
        );
      });
  }

  function syncBackgroundVideos({ inactiveDelay = 0 } = {}) {
    playActiveBackgroundVideos();

    if (pauseInactiveMediaTimeout) {
      window.clearTimeout(pauseInactiveMediaTimeout);
      pauseInactiveMediaTimeout = null;
    }

    if (inactiveDelay > 0) {
      pauseInactiveMediaTimeout = window.setTimeout(() => {
        pauseInactiveBackgroundVideos();
        pauseInactiveMediaTimeout = null;
      }, inactiveDelay);

      return;
    }

    pauseInactiveBackgroundVideos();
  }

  function playActiveBackgroundVideos() {
    if (!heroInView) {
      return;
    }

    if (!mediaStarted) {
      startMediaWhenIdle();
      return;
    }

    const backgroundVideos = carouselElement.querySelectorAll(
      ".hero-carousel__video",
    );

    backgroundVideos.forEach((video) => {
      const slide = video.closest(".swiper-slide");
      const isActive = slide && slide.classList.contains("swiper-slide-active");

      if (!isActive) return;

      if (shouldHoldPoster(video)) {
        video.pause();
        return;
      }

      ensureSelfHostedVideoSrc(video);
      playSelfHostedVideo(video);
    });

    youtubeShells.forEach((shell) => {
      if (isYouTubeShellActive(shell)) {
        if (prefersLightweightMedia()) {
          hideYouTubeFrame(shell);
          return;
        }

        const frame = mountYouTubeIframe(shell, { autoplay: true });

        if (!frame) {
          return;
        }

        const existingPlayer = youtubePlayers.get(shell);

        if (existingPlayer) {
          existingPlayer.mute();
          existingPlayer.playVideo();
          revealYouTubeFrameIfAlreadyPlaying(shell, existingPlayer);
          return;
        }

        sendYouTubeCommand(frame, "mute");
        sendYouTubeCommand(frame, "playVideo");
        queueYouTubePlaybackFallback(shell, frame);

        if (!canUseYouTubeApi()) {
          revealYouTubeFrame(shell, youtubeMobileRevealDelayMs);
          return;
        }

        ensureYouTubePlayer(shell, frame);
        return;
      }

      const player = youtubePlayers.get(shell);

      if (player) {
        player.mute();
      }
    });
  }

  function pauseInactiveBackgroundVideos() {
    const backgroundVideos = carouselElement.querySelectorAll(
      ".hero-carousel__video",
    );

    backgroundVideos.forEach((video) => {
      const slide = video.closest(".swiper-slide");
      const isActive = slide && slide.classList.contains("swiper-slide-active");

      if (isActive) {
        return;
      }

      video.pause();
      video.currentTime = 0;
      video.classList.remove("hero-carousel__video--playing");
    });

    youtubeShells.forEach((shell) => {
      if (isYouTubeShellActive(shell)) {
        return;
      }

      hideYouTubeFrame(shell);

      const player = youtubePlayers.get(shell);

      if (player) {
        player.pauseVideo();
        player.seekTo(0, true);
        return;
      }

      const frame = getYouTubeFrame(shell);

      if (!frame) {
        return;
      }

      sendYouTubeCommand(frame, "pauseVideo");
      sendYouTubeCommand(frame, "seekTo", [0, true]);
    });
  }

  function getYouTubeFrame(shell) {
    if (!shell) return null;

    return shell.querySelector(".hero-carousel__video-frame");
  }

  function mountYouTubeIframe(shell, { autoplay = false } = {}) {
    if (!shell) return null;

    const existingFrame = getYouTubeFrame(shell);
    if (existingFrame) {
      ensureYouTubePlayer(shell, existingFrame);
      return existingFrame;
    }

    const src =
      autoplay && shell.dataset.youtubeAutoplaySrc
        ? shell.dataset.youtubeAutoplaySrc
        : shell.dataset.youtubeSrc;
    if (!src) {
      return null;
    }

    const frame = document.createElement("iframe");
    frame.className = "hero-carousel__video-frame";
    frame.src = src;
    frame.title = shell.dataset.youtubeTitle || "Hero background video";
    frame.allow = "autoplay; encrypted-media";
    frame.loading = shell.dataset.loading || "lazy";
    frame.tabIndex = -1;
    frame.referrerPolicy = "strict-origin-when-cross-origin";
    frame.setAttribute("aria-hidden", "true");

    shell.appendChild(frame);
    ensureYouTubePlayer(shell, frame);

    return frame;
  }

  function syncYouTubePlayerPlayback(shell, player) {
    if (!shell || !player) {
      return;
    }

    player.mute();

    if (isYouTubeShellActive(shell)) {
      player.playVideo();
      revealYouTubeFrameIfAlreadyPlaying(shell, player);
      return;
    }

    hideYouTubeFrame(shell);
    player.pauseVideo();
    player.seekTo(0, true);
  }

  function clearYouTubeRevealTimer(shell) {
    const timer = youtubeRevealTimers.get(shell);

    if (timer) {
      window.clearTimeout(timer);
      youtubeRevealTimers.delete(shell);
    }
  }

  function clearYouTubePlaybackFallbackTimer(shell) {
    const timer = youtubePlaybackFallbackTimers.get(shell);

    if (timer) {
      window.clearTimeout(timer);
      youtubePlaybackFallbackTimers.delete(shell);
    }
  }

  function hideYouTubeFrame(shell) {
    if (!shell) {
      return;
    }

    clearYouTubeRevealTimer(shell);
    clearYouTubePlaybackFallbackTimer(shell);
    shell.classList.remove(
      "hero-carousel__video-shell--loaded",
      "hero-carousel__video-shell--failed",
    );
  }

  function failYouTubeFrame(shell) {
    if (!shell) {
      return;
    }

    clearYouTubeRevealTimer(shell);
    clearYouTubePlaybackFallbackTimer(shell);
    shell.classList.remove("hero-carousel__video-shell--loaded");
    shell.classList.add("hero-carousel__video-shell--failed");
  }

  function revealYouTubeFrame(shell, delay = youtubeRevealDelayMs) {
    if (!shell || !isYouTubeShellActive(shell)) {
      return;
    }

    clearYouTubeRevealTimer(shell);
    shell.classList.remove("hero-carousel__video-shell--failed");

    const timer = window.setTimeout(() => {
      if (isYouTubeShellActive(shell)) {
        shell.classList.add("hero-carousel__video-shell--loaded");
      }

      youtubeRevealTimers.delete(shell);
    }, delay);

    youtubeRevealTimers.set(shell, timer);
  }

  function revealYouTubeFrameIfAlreadyPlaying(shell, player) {
    if (!shell || !player || !window.YT?.PlayerState) {
      return;
    }

    if (
      typeof player.getPlayerState === "function" &&
      player.getPlayerState() === window.YT.PlayerState.PLAYING
    ) {
      revealYouTubeFrame(shell);
    }
  }

  function queueYouTubePlaybackFallback(shell, frame) {
    if (!shell || !frame) {
      return;
    }

    clearYouTubePlaybackFallbackTimer(shell);

    const timer = window.setTimeout(() => {
      if (isYouTubeShellActive(shell) && !youtubePlayers.get(shell)) {
        sendYouTubeCommand(frame, "mute");
        sendYouTubeCommand(frame, "playVideo");
      }

      youtubePlaybackFallbackTimers.delete(shell);
    }, 500);

    youtubePlaybackFallbackTimers.set(shell, timer);
  }

  function ensureYouTubePlayer(shell, frame) {
    if (!shell || !frame || !canUseYouTubeApi()) {
      return Promise.resolve(null);
    }

    const existingPlayer = youtubePlayers.get(shell);

    if (existingPlayer) {
      return Promise.resolve(existingPlayer);
    }

    const existingPromise = youtubePlayerPromises.get(shell);

    if (existingPromise) {
      return existingPromise;
    }

    const playerPromise = loadYouTubeIframeApi()
      .then((YT) => {
        if (!YT || typeof YT.Player !== "function") {
          return null;
        }

        return new Promise((resolve) => {
          let resolved = false;

          const finalize = (playerInstance) => {
            if (resolved) {
              return;
            }

            resolved = true;
            resolve(playerInstance);
          };

          const player = new YT.Player(frame, {
            events: {
              onReady: (event) => {
                youtubePlayers.set(shell, event.target);
                clearYouTubePlaybackFallbackTimer(shell);
                syncYouTubePlayerPlayback(shell, event.target);
                finalize(event.target);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  revealYouTubeFrame(shell);
                  return;
                }

                if (
                  event.data === YT.PlayerState.ENDED &&
                  isYouTubeShellActive(shell)
                ) {
                  event.target.seekTo(0, true);
                  event.target.playVideo();
                }
              },
              onError: () => {
                failYouTubeFrame(shell);
                finalize(null);
              },
            },
          });

          window.setTimeout(() => {
            finalize(player);
          }, 3000);
        });
      })
      .catch(() => null);

    youtubePlayerPromises.set(shell, playerPromise);

    return playerPromise;
  }

  function sendYouTubeCommand(frame, command, args = []) {
    if (!frame || !frame.contentWindow) return;

    let targetOrigin = "https://www.youtube.com";

    try {
      targetOrigin = new URL(frame.src, window.location.origin).origin;
    } catch (error) {
      targetOrigin = "https://www.youtube.com";
    }

    frame.contentWindow.postMessage(
      JSON.stringify({
        event: "command",
        func: command,
        args,
      }),
      targetOrigin,
    );
  }

  // Pause without rewinding: the hero is only off screen, so it should pick up
  // where it left off rather than restart when the visitor scrolls back.
  function suspendBackgroundMedia() {
    if (pauseInactiveMediaTimeout) {
      window.clearTimeout(pauseInactiveMediaTimeout);
      pauseInactiveMediaTimeout = null;
    }

    carouselElement
      .querySelectorAll(".hero-carousel__video")
      .forEach((video) => {
        video.pause();
      });

    youtubeShells.forEach((shell) => {
      const player = youtubePlayers.get(shell);

      if (player) {
        player.pauseVideo();
        return;
      }

      const frame = getYouTubeFrame(shell);

      if (frame) {
        sendYouTubeCommand(frame, "pauseVideo");
      }
    });
  }

  // A muted background video left decoding behind the rest of the page is one
  // of the more expensive things a phone can be asked to do for nothing, so
  // media and autoplay both stop once the hero scrolls away.
  if (typeof window.IntersectionObserver === "function") {
    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting === heroInView) {
            return;
          }

          heroInView = entry.isIntersecting;

          const autoplay =
            hasMultipleSlides && heroSwiper ? heroSwiper.autoplay : null;

          if (heroInView) {
            syncBackgroundVideos();

            if (autoplay) {
              autoplay.resume();
            }

            return;
          }

          suspendBackgroundMedia();

          if (autoplay) {
            autoplay.pause();
          }
        });
      },
      { rootMargin: "200px 0px" },
    );

    visibilityObserver.observe(carouselElement);
  }

  document.addEventListener("visibilitychange", () => {
    // Mobile browsers suspend background video while the tab or app is away,
    // so the active slide's media needs nudging back to life on return.
    if (!document.hidden) {
      syncBackgroundVideos();
    }

    if (!heroSwiper || !heroSwiper.autoplay || !hasMultipleSlides) return;

    if (document.hidden) {
      heroSwiper.autoplay.pause();
      pauseProgressBars();
      pauseMobileProgressBar();
    } else {
      heroSwiper.autoplay.resume();
      resumeProgressBars();
      resumeMobileProgressBar();
    }
  });

  function startProgressBar(activeIndex) {
    if (!hasNavigationCards || !hasMultipleSlides) return;

    const activeCard = navigationCards[activeIndex];
    if (!activeCard) return;

    activeCard.classList.add("hero-carousel__card--progress");
    activeCard.style.setProperty("--progress-duration", "10500ms");
  }

  function resetPreviousProgressBars(activeIndex) {
    if (!hasNavigationCards) return;

    navigationCards.forEach((card, index) => {
      if (index === activeIndex) return;

      card.classList.remove("hero-carousel__card--progress");
      card.style.removeProperty("--progress-duration");
      card.style.removeProperty("--progress-state");
    });
  }

  function pauseProgressBars() {
    if (!heroSwiper || !hasNavigationCards) return;

    const activeCard = navigationCards[heroSwiper.activeIndex];

    if (activeCard) {
      activeCard.style.setProperty("--progress-state", "paused");
    }
  }

  function resumeProgressBars() {
    if (!heroSwiper || !hasNavigationCards) return;

    const activeCard = navigationCards[heroSwiper.activeIndex];

    if (activeCard) {
      activeCard.style.setProperty("--progress-state", "running");
    }
  }

  // The navigation is pulled up over the bottom of the carousel with a
  // negative margin, so each slide has to reserve its height plus 3rem of
  // clearance underneath the slide content. Every slide renders its own
  // `__inner`, so all of them need the padding - reserving it on the first one
  // alone left the cards sitting on top of slides two and up.
  function adjustCarouselSpacing() {
    if (spacingUpdatePending) return;
    spacingUpdatePending = true;

    requestAnimationFrame(() => {
      spacingUpdatePending = false;

      const innerContainers = Array.from(
        carouselElement.querySelectorAll(".hero-carousel__inner"),
      );
      const heroNavigation = carouselElement.querySelector(
        ".hero-carousel__navigation",
      );
      // The SCSS hides the navigation below `lg`, where it needs no space at
      // all. Reading the rendered height rather than re-testing that
      // breakpoint in JS keeps the two from drifting apart, and it covers the
      // hero being hidden for any other reason too.
      const navigationHeight = heroNavigation ? heroNavigation.offsetHeight : 0;

      if (!hasNavigationCards || !heroNavigation || navigationHeight === 0) {
        lastNavigationHeight = null;

        innerContainers.forEach((innerContainer) => {
          innerContainer.style.paddingBottom = "";
        });

        if (heroNavigation) {
          heroNavigation.style.marginTop = "";
        }

        return;
      }

      if (navigationHeight === lastNavigationHeight) {
        return;
      }

      lastNavigationHeight = navigationHeight;

      innerContainers.forEach((innerContainer) => {
        innerContainer.style.paddingBottom = `calc(${navigationHeight}px + 3rem)`;
      });

      // 1px of overlap so the gradient behind the cards meets the slide above
      // it without a hairline of background showing through the seam.
      heroNavigation.style.marginTop = `${1 - navigationHeight}px`;
    });
  }

  // The card grid changes height without the window ever being resized: it
  // drops from four columns to two below 1200px, and the webfonts reflow the
  // card titles well after the carousel has initialised. Measuring once at
  // init is why the reserved space went stale at some widths.
  const navigationElement = carouselElement.querySelector(
    ".hero-carousel__navigation",
  );

  if (navigationElement && typeof window.ResizeObserver === "function") {
    // Safe to observe: the only things written back are the navigation's
    // margin and the slides' padding, neither of which changes the
    // navigation's own box, so this cannot feed back into itself.
    const spacingObserver = new ResizeObserver(() => {
      adjustCarouselSpacing();
    });

    spacingObserver.observe(navigationElement);
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      adjustCarouselSpacing();
      equalizeSlideHeights();
    });
  }

  // Fallback for browsers without ResizeObserver, and a second pass once the
  // first layout has settled.
  setTimeout(() => {
    adjustCarouselSpacing();
  }, 100);

  function equalizeSlideHeights() {
    if (heightUpdatePending) return;
    heightUpdatePending = true;

    requestAnimationFrame(() => {
      if (slides.length === 0) {
        heightUpdatePending = false;
        return;
      }

      slides.forEach((slide) => {
        slide.style.height = "auto";
      });

      requestAnimationFrame(() => {
        let maxHeight = 0;

        slides.forEach((slide) => {
          const slideHeight = slide.offsetHeight;

          if (slideHeight > maxHeight) {
            maxHeight = slideHeight;
          }
        });

        slides.forEach((slide) => {
          slide.style.height = `${maxHeight}px`;
        });

        heightUpdatePending = false;
      });
    });
  }

  function startMobileProgressBar() {
    if (!mobileProgressFill || !hasMultipleSlides) return;

    mobileProgressFill.classList.remove(
      "hero-carousel__progress-fill--animating",
    );
    mobileProgressFill.style.removeProperty("--progress-duration");
    mobileProgressFill.style.removeProperty("--progress-state");
    mobileProgressFill.style.width = "0%";
    mobileProgressFill.style.transition = "none";
    mobileProgressFill.offsetHeight;

    setTimeout(() => {
      mobileProgressFill.style.transition = "";
      mobileProgressFill.classList.add(
        "hero-carousel__progress-fill--animating",
      );
      mobileProgressFill.style.setProperty("--progress-duration", "10500ms");
      mobileProgressFill.style.setProperty("--progress-state", "running");
    }, 100);
  }

  function pauseMobileProgressBar() {
    if (!mobileProgressFill || !hasMultipleSlides) return;

    mobileProgressFill.style.setProperty("--progress-state", "paused");
  }

  function resumeMobileProgressBar() {
    if (!mobileProgressFill || !hasMultipleSlides) return;

    mobileProgressFill.style.setProperty("--progress-state", "running");
  }

  window.addEventListener("resize", () => {
    adjustCarouselSpacing();
    equalizeSlideHeights();
    syncBackgroundVideos();
  });

  return heroSwiper;
}
